import React from 'react';
import { TimePeriods, BucketPeriods, BucketUnits } from 'utils/ReadingsHelpers';
import { Roles } from 'utils/authHelpers';
import { setTelemetryPeriod } from 'redux/Telemetry';
import SwaggerAPI from 'data/SwaggerAPI';
import _ from 'lodash';
import {
  SpaOutlined,
  AgricultureOutlined,
  BackpackOutlined,
  WaterDropOutlined,
  LayersOutlined,
  MedicalInformationOutlined,
  QuestionMarkOutlined,
  SignalCellular0BarTwoTone,
  SignalCellular4BarTwoTone,
  SignalCellularConnectedNoInternet0BarTwoTone,
} from '@mui/icons-material';
import RegEx from './RegEx';
import ReactGA from 'react-ga4';
import { Icon, SvgIcon } from '@mui/material';

const ReportEventToAnalytics = (category, action, label) => {
  if (process.env.NODE_ENV && process.env.NODE_ENV === 'production') {
    ReactGA.event({ category: category, action: action, label: label });
  }
};

const DashboardHelpers = (function () {
  const dateToKey = (date) => {
    const dateToday = new Date(date);

    const years = dateToday.getFullYear();
    let month = (dateToday.getMonth() + 1).toString().padStart(2, '0');
    let day = dateToday.getDate().toString().padStart(2, '0');
    const dateArray = [years, month, day];

    return dateArray.join('-') + 'T00:00:00Z';
  };

  const formatTimestamp = (date) => {
    const years = date.getFullYear();
    let month = (date.getMonth() + 1).toString().padStart(2, '0');
    let day = date.getDate().toString().padStart(2, '0');
    const dateArray = [years, month, day];

    return dateArray.join('-') + 'T00:00:00Z';
  };

  /**
   * filters fields by selected property
   *
   * @param {string} property_id
   * @param {string} organization_id
   * @param {object} fields
   * @returns fields based on the property or organization ID
   */
  const filteredFields = (property_id, organization_id, fields) => {
    if (property_id) {
      return _.filter(fields, { property_id: property_id });
    }

    if (organization_id) {
      return _.filter(fields, { organization_id: organization_id });
    }

    return [];
  };

  /**
   * Formats a date string to a readable format.
   *
   * @param {string} dateTime
   * @returns string formatted date, if the date passed in was not a valid date, it will return an empty string.
   */
  const formatDateTime = (dateTime) => {
    let formatted = dateTime;
    if (typeof dateTime === 'string' && new Date(dateTime).getTime() > 0) {
      const timestamp = new Date(dateTime);

      const options = {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour12: true,
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      };

      formatted = timestamp.toLocaleString('en-US', options);
    }
    return formatted ? formatted : '';
  };

  const constructDateKeys = () => {
    const now = new Date();

    let keys = {};

    const dateKeyToday = dateToKey(now);
    keys.today = dateKeyToday;

    const yesterday = new Date(now.setDate(now.getDate() - 1));
    const yesterdayKey = dateToKey(yesterday);
    keys.yesterday = yesterdayKey;

    const lastWeek = new Date(now.setDate(now.getDate() - 6));
    const lastWeekKey = formatTimestamp(lastWeek);
    keys.lastWeek = lastWeekKey;
    return keys;
  };

  const getTelemetryAverageStats = (telemetryAverages, telemetryPeriod, selectedField, selectedDevice) => {
    let key;
    if (telemetryAverages) {
      const buckets = constructDateKeys();
      switch (telemetryPeriod) {
        case TimePeriods.OneWeek:
          key = buckets.lastWeek;
          break;
        case TimePeriods.TwoDays:
          key = buckets.yesterday;
          break;
        case TimePeriods.OneDay:
          key = buckets.today;
          break;
        default:
          key = buckets.today;
          break;
      }
      if (!process.env.NODE_ENV || process.env.NODE_ENV === 'development') {
        console.log('selectedField', selectedField, 'selectedDevice', selectedDevice, 'telemetry', telemetryAverages);
      }
      if (selectedDevice) {
        if (
          !!telemetryAverages.devices[selectedDevice.hw_address] &&
          !!telemetryAverages.devices[selectedDevice.hw_address][key]
        ) {
          return telemetryAverages.devices[selectedDevice.hw_address][key].data;
        }
        return {};
      } else if (
        selectedField &&
        !!telemetryAverages.fields[selectedField.id] &&
        !!telemetryAverages.fields[selectedField.id][key]
      ) {
        if (!process.env.NODE_ENV || process.env.NODE_ENV === 'development') {
          console.log('active field Telemetry', telemetryAverages.fields[selectedField.id][key]);
        }
        return telemetryAverages.fields[selectedField.id][key].data;
      }
    }
    return {};
  };

  /**
   * Returns the telemetry averages for the selected device or field
   * @param {object} telemetryDeviceAverage the object containing the telemetry averages for the selected device
   * @param {object} telemetryFieldAverage the object containing the telemetry averages for the selected field
   * @param {string} selectedDevice the selected device
   * @param {object} selectedField the selected field
   * @param {number} telemetryPeriod the time period selected in seconds
   * @returns {object} return an object with the keys {dataSet,readingTypes,tick,graphDomain}
   */
  const getDataSet = (
    telemetryDeviceAverage,
    telemetryFieldAverage,
    selectedDevice,
    selectedField,
    telemetryPeriod
  ) => {
    let incrementTime;
    let dataSet = {};
    const now = new Date();
    const reallyNow = ~~(now.getTime() / 1000);
    let graphDomain = undefined;

    let chartStart = reallyNow;
    let telemtryVals = telemetryDeviceAverage ? telemetryDeviceAverage : telemetryFieldAverage;
    _.forEach(telemtryVals, (value, key) => {
      let date = new Date(key);
      let timestamp = date.getTime() / 1000;
      if (timestamp < chartStart) {
        chartStart = timestamp;
      }
    });

    let chartEnd = chartStart;
    _.forEach(telemtryVals, (value, key) => {
      let date = new Date(key);
      let timestamp = date.getTime() / 1000;
      if (timestamp > chartEnd) {
        chartEnd = timestamp;
      }
    });

    switch (telemetryPeriod) {
      case TimePeriods.OneDay:
        incrementTime = TimePeriods.OneHourSeconds;
        graphDomain = { start: chartStart, end: chartEnd };
        break;
      case TimePeriods.OneWeek:
        incrementTime = TimePeriods.FourHoursSeconds;
        graphDomain = { start: chartStart, end: chartEnd };
        break;
      case TimePeriods.OneMonth:
        incrementTime = TimePeriods.FourHoursSeconds;
        graphDomain = { start: chartStart, end: chartEnd };
        break;
      case TimePeriods.ThreeMonth:
        incrementTime = TimePeriods.OneDaySeconds;
        graphDomain = { start: chartStart, end: chartEnd };
        break;
      case TimePeriods.OneYear:
        incrementTime = TimePeriods.OneMonthSeconds;
        graphDomain = { start: chartStart, end: chartEnd };
        break;
      case TimePeriods.TwentyYears:
        incrementTime = TimePeriods.OneMonthSeconds;
        graphDomain = { start: chartStart, end: chartEnd };
        break;
      default:
        incrementTime = TimePeriods.OneDaySeconds;
        graphDomain = { start: chartStart, end: chartEnd };
    }

    let telemetryAverage = undefined;

    if (selectedDevice && selectedDevice.id !== StatusCodes.NoDeviceSelected && selectedDevice.id !== undefined) {
      const activeDevTel = telemetryDeviceAverage;
      telemetryAverage = activeDevTel ? activeDevTel : {};
    } else if (selectedField && (selectedField.id !== StatusCodes.NoFieldSelected || selectedField.id !== undefined)) {
      const selectedFieldTel = telemetryFieldAverage;
      telemetryAverage = selectedFieldTel ? selectedFieldTel : {};
    }

    const { desiredReadings, initAvg } = getDesiredReading(telemetryAverage, graphDomain);

    // Only create data points for timestamps where we have actual telemetry data
    let firstTime;
    if (telemetryAverage !== undefined) {
      _.forEach(telemetryAverage, (timedReadings, time) => {
        _.forEach(timedReadings.data, (value, key) => {
          _.forEach(value, (depthAvg, depth) => {
            if (!firstTime) {
              firstTime = time;
            }
            const convDate = new Date(time).getTime() / 1000;
            let type = '';

            if (depth == 0) {
              type = key;
            } else {
              type = key + '_' + depth;
            }

            if (convDate >= graphDomain.start && convDate <= graphDomain.end) {
              let roundedTime;
              if (
                telemetryPeriod === TimePeriods.OneDay ||
                telemetryPeriod === TimePeriods.OneWeek ||
                telemetryPeriod === TimePeriods.OneMonth
              ) {
                // Round to nearest 4-hour interval (4, 8, 12) for 1-day and 1-week views
                const date = new Date(convDate * 1000);
                const hour = date.getHours();
                const roundedHour = Math.floor(hour / 4) * 4;
                date.setHours(roundedHour, 0, 0, 0);
                roundedTime = Math.floor(date.getTime() / 1000);
              } else {
                // Use increment time for other periods
                roundedTime = convDate - (convDate % incrementTime);
              }

              if (!dataSet[roundedTime]) {
                dataSet[roundedTime] = { ...initAvg, timestamp: roundedTime };
              }
              if (
                depthAvg.value != undefined &&
                typeof depthAvg.value == 'number' &&
                Object.keys(dataSet[roundedTime]).includes(type)
              ) {
                if (type === 'co2_6' > 0) {
                  dataSet[roundedTime][type] = depthAvg.value;
                } else {
                  dataSet[roundedTime][type] = depthAvg.value;
                }
              } else if (depthAvg.value === null) {
                dataSet[roundedTime][type] = depthAvg.value;
              }
            }
          });
        });
      });
    }

    let returnSet = [];
    // Make sure the graph starts according to the time period selected - TL-372
    if (firstTime > graphDomain.start) {
      returnSet.push({ timestamp: graphDomain.start });
    }
    _.forEach(dataSet, (averages) => {
      const returnValues = { timestamp: averages.timestamp };
      _.forEach(desiredReadings, (reading) => {
        if (_.has(averages, reading)) {
          returnValues[reading] = averages[reading];
        }
      });
      returnSet.push(returnValues);
    });
    const returnObject = {
      dataSet: returnSet,
      readingTypes: desiredReadings,
      graphDomain: graphDomain,
    };
    return returnObject;
  };

  const getDesiredReading = (telemetryAverage, graphDomain) => {
    let desiredReadings = [];
    let initAvg = {};

    if (telemetryAverage === undefined || graphDomain === undefined) {
      return [];
    } else {
      _.forEach(telemetryAverage, (timedReadings) => {
        _.forEach(timedReadings.data, (value, key) => {
          _.forEach(value, (_, depth) => {
            let type;
            if (depth == 0) {
              type = key;
            } else {
              type = key + '_' + depth;
            }

            if (!desiredReadings.includes(type)) {
              desiredReadings.push(type);
            }

            initAvg[type] = undefined;
          });
        });
      });
    }
    return { desiredReadings, initAvg };
  };

  const exportData = async (csv, filename) => {
    if (csv) {
      var blob = new Blob([csv], { type: 'text/json;charset=utf-8;' });
      if (navigator.msSaveBlob) {
        // IE 10+
        navigator.msSaveBlob(blob, filename);
      } else {
        var link = document.createElement('a');
        if (link.download !== undefined) {
          // feature detection
          // Browsers that support HTML5 download attribute
          var url = URL.createObjectURL(blob);
          link.setAttribute('href', url);
          link.setAttribute('download', filename);
          link.style.visibility = 'hidden';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }
      }
    }
  };

  /**
   * Exports the telemetry data for a single device to a CSV file
   * @param {object} selectedDevice
   * @returns none
   */
  const exportDataSingle = async (selectedDevice) => {
    if (selectedDevice.hw_address) {
      const json = await SwaggerAPI.getDeviceTelemetry(selectedDevice, BucketPeriods.ThreehundredSixtyFive, undefined, BucketPeriods.One, BucketUnits.Days, true); // eslint-disable-line
      if (!process.env.NODE_ENV || process.env.NODE_ENV === 'development') {
        console.log('fetchDeviceSingularTelemetryAPI json', json);
      }

      const csv = jsonToCSV(json['telemetry_averages']);

      var exportedFilenmae = selectedDevice.hw_address + '.csv' || 'export.csv';

      var blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      if (navigator.msSaveBlob) {
        // IE 10+
        navigator.msSaveBlob(blob, exportedFilenmae);
      } else {
        var link = document.createElement('a');
        if (link.download !== undefined) {
          // feature detection
          // Browsers that support HTML5 download attribute
          var url = URL.createObjectURL(blob);
          link.setAttribute('href', url);
          link.setAttribute('download', exportedFilenmae);
          link.style.visibility = 'hidden';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }
      }
    }
  };

  /**
   * Gets the parent layer based on a layer id.
   * @param {object} layer the layer you are getting the parent of
   * @param {string} layerId the id of the layer you are getting the parent of
   * @param {object} devices the devices object that has all the devices to search through for parent
   * @param {object} fields the fields object that has all the fields to search through for parent
   * @param {object} properties the properties object that has all the properties to search through for parent
   * @returns {object,object} returns the field and property objects of the parent layers
   */
  const getParentsOfLayer = (layer, layerId, devices, fields, properties) => {
    let property;
    let field;

    if (layer === 'device') {
      let device = getObjectById(layerId, undefined, undefined, undefined, devices);

      if (device?.field_id) {
        field = getObjectById(device.field_id, undefined, undefined, fields, undefined);

        if (field) {
          property = getObjectById(field.property_id, undefined, properties, undefined, undefined);
        }
      }
    } else if (layer === 'field') {
      field = getObjectById(layerId, undefined, undefined, fields, undefined);
      if (field?.property_id) {
        property = getObjectById(field.property_id, undefined, properties, undefined, undefined);
      }
    }
    return { field, property };
  };

  /**
   * Gets and event and the events subordfinate events
   * @param {string} activeObjectId the string of the active event ID
   * @param {string} layer  the layer of the active event
   * @param {object} events the events object that has all the devices to search through to compare event ID's
   * @param {object} properties the properties object that has all the devices to search through to compare event ID's
   * @param {object} devices the devices object that has all the devices to search through to compare event ID's
   * @returns { object, object, object } returns the an object with the following nested objects { counts, eventsByType, deduplicatedEventsByType }
   */
  const getEventsAndSubordinates = (activeObjectId, layer, events, properties, devices) => {
    const counts = { errors: 0, warnings: 0, info: 0 };
    const eventsByType = { error: [], warning: [], info: [] };
    const deduplicatedEventsByType = { error: [], warning: [], info: [] };
    // Get the events for the active object
    const activeLayerEvents = [];

    if (events[activeObjectId]) {
      activeLayerEvents.push(...events[activeObjectId]);
    }

    // Get the subordinates for the active object
    if (layer === 'organization') {
      const activeLayerProperties = getPropertiesForOrganization(activeObjectId, properties);
      _.forEach(activeLayerProperties, (property) => {
        const activeLayerPropertyEvents = getEventsForLayerArray(property.id, events);
        if (activeLayerPropertyEvents) {
          activeLayerEvents.push(...activeLayerPropertyEvents);
        }

        const activeLayerFields = getFieldsForProperty(property.id, properties);
        const activeLayerFieldEvents = getEventsForLayerArray(activeLayerFields, events);
        if (activeLayerFieldEvents) {
          activeLayerEvents.push(...activeLayerFieldEvents);
        }
        _.forEach(activeLayerFields, (field) => {
          const activeLayerDevices = getDevicesForField(field.id, devices);
          if (activeLayerDevices) {
            const activeLayerDeviceEvents = getEventsForLayerArray(activeLayerDevices, events);
            if (activeLayerDeviceEvents) {
              activeLayerEvents.push(...activeLayerDeviceEvents);
            }
          }
        });
      });
    } else if (layer === 'property') {
      const activeLayerFields = getFieldsForProperty(activeObjectId, properties);
      const activeLayerFieldEvents = getEventsForLayerArray(activeLayerFields, events);
      if (activeLayerFieldEvents) {
        activeLayerEvents.push(...activeLayerFieldEvents);
      }
      _.forEach(activeLayerFields, (field) => {
        const activeLayerDevices = getDevicesForField(field.id, devices);
        if (activeLayerDevices) {
          const activeLayerDeviceEvents = getEventsForLayerArray(activeLayerDevices, events);
          if (activeLayerDeviceEvents) {
            activeLayerEvents.push(...activeLayerDeviceEvents);
          }
        }
      });
    } else if (layer === 'field') {
      const activeLayerDevices = getDevicesForField(activeObjectId, devices);
      if (activeLayerDevices) {
        const activeLayerDeviceEvents = getEventsForLayerArray(activeLayerDevices, events);
        if (activeLayerDeviceEvents) {
          activeLayerEvents.push(...activeLayerDeviceEvents);
        }
      }
    }

    // Combine all the events
    if (activeLayerEvents) {
      _.forEach(activeLayerEvents, (event) => {
        if (event.type === 'error') {
          counts.errors += 1;
          if (
            !_.find(eventsByType.error, {
              layer: event.layer,
              layer_id: event.layer_id,
              type: event.type,
              created_by: event.created_by,
            })
          ) {
            const evLikeThis = _.filter(activeLayerEvents, {
              layer: event.layer,
              layer_id: event.layer_id,
              type: event.type,
              created_by: event.created_by,
            });
            event['likeThis'] = evLikeThis.length;
            deduplicatedEventsByType.error.push(event);
          }
          eventsByType.error.push(event);
        } else if (event.type === 'warning' || event.type === 'warn') {
          counts.warnings += 1;
          if (
            !_.find(eventsByType.warning, {
              layer: event.layer,
              layer_id: event.layer_id,
              type: event.type,
              created_by: event.created_by,
            })
          ) {
            const evLikeThis = _.filter(activeLayerEvents, {
              layer: event.layer,
              layer_id: event.layer_id,
              type: event.type,
              created_by: event.created_by,
            });
            event['likeThis'] = evLikeThis.length - 1;
            deduplicatedEventsByType.warning.push(event);
          }
          eventsByType.warning.push(event);
        } else if (event.type === 'info') {
          counts.info += 1;
          if (
            !_.find(eventsByType.info, {
              layer: event.layer,
              layer_id: event.layer_id,
              type: event.type,
              created_by: event.created_by,
            })
          ) {
            const evLikeThis = _.filter(activeLayerEvents, {
              layer: event.layer,
              layer_id: event.layer_id,
              type: event.type,
              created_by: event.created_by,
            });
            event['likeThis'] = evLikeThis.length;
            deduplicatedEventsByType.info.push(event);
          }
          eventsByType.info.push(event);
        }
      });
    }

    return { counts, eventsByType, deduplicatedEventsByType };
  };

  const getEventsForLayerArray = (layerArray, events) => {
    const arrayEvents = [];
    _.forEach(layerArray, (layer) => {
      const layerEvents = events[layer.id];
      if (layerEvents) {
        arrayEvents.push(...layerEvents);
      }
    });
    return arrayEvents;
  };

  const getFieldsForProperty = (activeObjectId, properties) => {
    const propertyFields = _.find(properties, { id: activeObjectId })?.fields;

    return propertyFields;
  };

  const getPropertiesForOrganization = (activeObjectId, properties) => {
    const organizationProperties = _.filter(properties, { organization_id: activeObjectId });

    return organizationProperties;
  };

  const getDevicesForField = (fieldId, devices) => {
    const fieldDevices = [];
    _.forEach(devices, (device) => {
      if (device.field_id === fieldId) {
        fieldDevices.push(device);
      }
    });
    return fieldDevices;
  };

  const jsonToCSV = (items) => {
    let csv = '';
    let headers = [];
    for (let timestamp in items) {
      let row = {};
      let telemetry = items[timestamp].data;

      // Iterate over each telemetry item
      for (let item in telemetry) {
        let subTelemetry = telemetry[item];

        // Iterate over each sub telemetry item
        for (let subItem in subTelemetry) {
          let value = subTelemetry[subItem].value;

          // If the value is null, set it to an empty string
          if (value === null) {
            value = '';
          }

          // Add the value to the row
          row[item + '_' + subItem] = value;

          // Add the header if it doesn't already exist
          if (headers.indexOf(item + '_' + subItem) === -1) {
            headers.push(item + '_' + subItem);
          }
        }
      }

      // Add the timestamp and row to the CSV
      csv += timestamp + ',' + headers.map((header) => row[header] || '').join(',') + '\n';
    }

    // Add the headers to the beginning of the CSV
    csv = 'timestamp,' + headers.join(',') + '\n' + csv;

    return csv;
  };

  const getEventsForLayers = (layer_ids, events) => {
    if (layer_ids && events && layer_ids.length > 0 && events.length > 0) {
      let eventsFiltered = [];
      _.forEach(layer_ids, (id) => {
        eventsFiltered = eventsFiltered.concat(_.filter(events, { layer_id: id }));
      });

      return eventsFiltered;
    }
  };

  /**
   * converts a string and search text to lower case and then searches for the search text in the string
   * @param {string} startingString string to search on
   * @param {string} searchText string to search for
   * @returns bool
   */
  const includesCaseInsensitive = (startingString, searchText) => {
    return startingString ? startingString.toLowerCase().includes(searchText.toLowerCase()) : false;
  };

  /**
   * takes the active tab and sets the default telemetry period based on the active tab
   * @param {string} activeTab the active tab
   * @returns none
   */
  const setDefaultTelemetryPeriodByActiveTab = (activeTab) => {
    switch (activeTab) {
      case 'stats':
        setTelemetryPeriod(TimePeriods.ZeroDays);
        break;
      case 'charts':
        setTelemetryPeriod(TimePeriods.OneWeek);
        break;
    }
  };

  /**
   * searches through organizations, properties, fields, and devices for a match to the id passed in
   * @param {string} id the id of the object you are searching for
   * @param {[object]} organizations array of organizations, defaults to empty array
   * @param {[object]} properties array of properties, defaults to empty array
   * @param {[object]} fields array of fields, defaults to empty array
   * @param {[object]} devices array of devices, defaults to empty array
   * @returns {object} the matching object
   */
  const getObjectById = (id, organizations = [], properties = [], fields = [], devices = []) => {
    const object = _.find([...organizations, ...properties, ...fields, ...devices], { id: id });

    return object;
  };

  /**
   * takes and object and does a shallow flatten of the object
   * @param {object} obj the object to flatten
   * @returns {object} the flattened object
   */
  const flattenObject = (obj) => {
    const flattened = {};
    Object.keys(obj).forEach((key) => {
      const value = obj[key];
      if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
        Object.assign(flattened, flattenObject(value));
      } else {
        flattened[key] = value;
      }
    });
    return flattened;
  };

  /**
   *
   * @param {object} formData the form data object
   * @param {string} field the field to validate
   * @param {object} formErrorBuilder the form error builder object
   * @param {string} errorMessage the error message to display if the field is not valid
   * @returns {object} and object with the formErrorBuilder built.
   */
  const validateFormField = (formData, field, formErrorBuilder, errorMessage) => {
    if (typeof formData[field] === 'string') {
      if (formData && field && formData[field] && formData[field].trim() !== '') {
        formErrorBuilder = { ...formErrorBuilder, [field]: '' };
      } else {
        formErrorBuilder = { ...formErrorBuilder, [field]: errorMessage };
        formData[field] = '';
      }
    } else if (formData[field] === undefined) {
      formErrorBuilder = { ...formErrorBuilder, [field]: errorMessage };
    } else if (formData[field] !== undefined) {
      formErrorBuilder = { ...formErrorBuilder, [field]: '' };
    }

    return formErrorBuilder;
  };

  /**
   *
   * @param {object} events the events object that has all the events by type
   * @param {object} type the type of event to get
   * @param {object} data the data object that has all the organizations, properties, fields, and devices
   * @returns {[objects]} list of components to display in the tooltip
   */
  const getEventGlanceTooltipListByType = (events, type, data) => {
    if (!events || !events[type]) {
      return [];
    }
    const { organizations, properties, fields, devices } = data;
    const listComponents = [];
    _.forEach(events[type], (event) => {
      const obj = getObjectById(event.layer_id, organizations, properties, fields, devices);
      let title = '';
      if (obj && obj.name) {
        title = ' - ' + obj.name;
      } else if (obj.hw_address) {
        title = ' - ' + trimHWAddress(obj.hw_address);
      }
      listComponents.push(
        <div key={event.id}>
          {event.layer}
          {title}
          {' - '}
          {event.data.insight}
          {event.likeThis > 0 ? ' (' + event.likeThis + ') ' : ''}
        </div>
      );
    });

    return listComponents;
  };

  /**
   * @param {PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.shape({alias: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),sortable: PropTypes.bool.isRequired,format: PropTypes.oneOf(['date', 'string', 'number']),dataAlias: PropTypes.string.isRequired,className: PropTypes.string,dataRender: PropTypes.func}))} tableHeader an array of objects that are the table headers
   * @param {string} order asc or desc
   * @param {string} orderBy the alias of the column that is being sorted
   * @param {[object]} data array of objects that are being sorted
   * @returns {[object]} array of sorted objects
   */
  const sortTableFunction = (tableHeader, order, orderBy, data) => {
    const header = _.find(tableHeader, { dataAlias: orderBy });
    let sortedData = data;
    if (header?.format === 'string') {
      sortedData = _.sortBy(sortedData, [(o) => (o[orderBy] ? o[orderBy].toUpperCase().trim() : '')]);
    }
    if (order === 'asc') {
      sortedData.reverse();
    }

    return sortedData;
  };

  /**
   *
   * @param {[object]} fields array of fields
   * @returns {number[][]} lat long of the bounds of the array of fields passed in. If no fields are passed in, returns the default lat long of [[-90, 180], [90, -180]]
   */
  const getBounds = (fields) => {
    const latLngs = [
      [-90, 180],
      [90, -180],
    ];
    if (fields && _.size(fields) > 0) {
      _.forEach(fields, (field) => {
        if (field.geometry && field.geometry && field.geometry.coordinates) {
          _.forEach(field.geometry.coordinates, (c) => {
            let coordArray;
            if (field.geometry.type === 'MultiPolygon') {
              coordArray = c;
            } else {
              // Polygon has only one set of coords
              coordArray = [c];
            }
            _.forEach(coordArray, (coords) => {
              _.forEach(coords, (coord) => {
                if (coord[1] > latLngs[0][0]) {
                  latLngs[0][0] = coord[1];
                }
                if (coord[1] < latLngs[1][0]) {
                  latLngs[1][0] = coord[1];
                }
                if (coord[0] < latLngs[0][1]) {
                  latLngs[0][1] = coord[0];
                }
                if (coord[0] > latLngs[1][1]) {
                  latLngs[1][1] = coord[0];
                }
              });
            });
          });
        }
      });
    }
    return latLngs;
  };

  /**
   *
   * @param {object} field
   * @param {[object]} fields
   * @returns {number[][]} default lat/long of [[-90, 180], [90, -180]] if no field is passed in and no fields are in the array. Otherwise, returns the bounds of the field or fields passed in.
   */
  const getBoundsForField = (field, fields) => {
    const latLngs = [
      [-90, 180],
      [90, -180],
    ];
    if (!field && _.size(fields) === 0) {
      return latLngs;
    }

    if (field && field?.id) {
      if (field.geometry && field.geometry && field.geometry.coordinates) {
        _.forEach(field.geometry.coordinates, (c) => {
          let coordArray;
          if (field.geometry.type === 'MultiPolygon') {
            coordArray = c;
          } else {
            // Polygon has only one set of coords
            coordArray = [c];
          }
          _.forEach(coordArray, (coords) => {
            _.forEach(coords, (coord) => {
              if (coord[1] > latLngs[0][0]) {
                latLngs[0][0] = coord[1];
              }
              if (coord[1] < latLngs[1][0]) {
                latLngs[1][0] = coord[1];
              }
              if (coord[0] < latLngs[0][1]) {
                latLngs[0][1] = coord[0];
              }
              if (coord[0] > latLngs[1][1]) {
                latLngs[1][1] = coord[0];
              }
            });
          });
        });
      }
      return latLngs;
    }
    return getBounds(fields);
  };

  /**
   * searches over the roles passed in and returns the highest role in the array
   *
   * @param {array} roles
   * @returns string of the highest role in the array
   */
  const getHighestRole = (roles) => {
    if (roles?.includes(Roles.Super)) {
      return Roles.Super;
    }
    if (roles?.includes(Roles.Admin)) {
      return Roles.Admin;
    }
    return Roles.User;
  };

  const toProperCase = (str) => {
    if (!!str && str.length > 0) {
      return str[0].toUpperCase() + str.slice(1).toLowerCase();
    }

    return str;
  };

  const searchTime = (timestamp, search) => {
    const timeString = formatDateTime(timestamp);
    if (typeof timeString == 'string' && timeString.toLowerCase().includes(search)) {
      return true;
    }
    return false;
  };

  /**
   * Recursively searches an object for a string based upong the headers. It will only search the key:value pairs that fall under the headers.
   *
   * @param {object} obj to be searched
   * @param {string} search string being searched for
   * @param {[object]} header to limit the search to
   * @returns bool
   */
  const searchObject = (obj, search, headers) => {
    let found = [];
    if (!RegEx.isNonEmptyString(search)) {
      return true;
    }

    if (obj) {
      _.forEach(obj, (o, key) => {
        let foundInObject = searchProps(o, key, search, headers);
        found.push(foundInObject);
        if (foundInObject) {
          return true;
        }
      });
    }

    return found.includes(true);
  };

  const propIsInHeader = (prop, header) => {
    let includes = false;
    _.forEach(header, (h) => {
      if (h.dataAlias === prop && !h.noDisplay) {
        includes = true;
      }
    });

    return includes;
  };

  const removeColons = (search) => {
    return typeof search === 'string' ? search.toLowerCase().split(':').join('') : '';
  };

  const searchProps = (obj, key, search, headers) => {
    let found = false;
    if (RegEx.isUUID(obj)) {
      return false;
    }

    if (typeof obj === 'object') {
      _.forEach(obj, (val, k) => {
        if (searchProps(val, k, search, headers)) {
          found = true;
        }
      });
    } else {
      if (!propIsInHeader(key, headers)) {
        found = false;
        return false;
      }
      if (!isNaN(new Date(obj))) {
        if (searchTime(obj, search)) {
          found = true;
        }
      } else if (removeColons(obj).includes(removeColons(search))) {
        found = true;
      }
    }

    return found;
  };

  /**
   *
   * @param {string} planType
   * @returns {JSX.Element} an icon based on the plan type
   */
  const getIconForPlanType = (planType) => {
    switch (toProperCase(planType)) {
      case PlanTypes.Harvest:
        return <AgricultureOutlined fontSize="inherit" />;
      case PlanTypes.Plant:
        return <SpaOutlined fontSize="inherit" />;
      case PlanTypes.Fertilize:
        return <BackpackOutlined fontSize="inherit" />;
      case PlanTypes.Irrigate:
        return <WaterDropOutlined fontSize="inherit" />;
      case PlanTypes.Clear:
        return <LayersOutlined fontSize="inherit" />;
      case PlanTypes.Treat:
        return <MedicalInformationOutlined fontSize="inherit" />;
      default:
        return <QuestionMarkOutlined fontSize="inherit" />;
    }
  };

  /**
   *
   * @param {number} rssi
   * @returns {JSX.Element} an icon based on the rssi value
   */
  const getIconForRSSIValue = (rssiValue) => {
    if (rssiValue <= -120) {
      return <SignalCellularConnectedNoInternet0BarTwoTone sx={{ color: 'red' }} />;
    } else if (rssiValue > -120 && rssiValue < -100) {
      return <SignalCellular0BarTwoTone sx={{ color: 'red' }} />;
    } else if (rssiValue >= -100 && rssiValue < -80) {
      return (
        <Icon>
          <SvgIcon>
            <path fillOpacity=".3" d="M2 22h20V2L2 22z" fill="grey"></path>
            <path d="M12 12L2 22h10V12z" fill="red"></path>
          </SvgIcon>
        </Icon>
      );
    } else if (rssiValue >= -80 && rssiValue < -60) {
      return (
        <Icon>
          <SvgIcon>
            <path fillOpacity=".3" d="M2 22h20V2L2 22z" fill="grey"></path>
            <path d="M14 10L2 22h12V10z" fill="orange"></path>
          </SvgIcon>
        </Icon>
      );
    } else if (rssiValue >= -60 && rssiValue < -40) {
      return (
        <Icon>
          <SvgIcon>
            <path fillOpacity=".3" d="M2 22h20V2L2 22z" fill="grey"></path>
            <path d="M17 7L2 22h15V7z" fill="gold"></path>
          </SvgIcon>
        </Icon>
      );
    } else if (rssiValue >= -40) {
      return <SignalCellular4BarTwoTone sx={{ color: 'green' }} />;
    }
  };

  /**
   * Trims a string to the last 8 char's
   * @param {string} string - string to be formatted
   * @returns string 8 characters long
   */
  const trimHWAddress = (hwAddress) => {
    if (hwAddress && hwAddress.length > 8 && hwAddress.includes(':')) {
      return hwAddress.slice(hwAddress.length - 8, hwAddress.length);
    } else if (hwAddress && hwAddress.length > 6 && !hwAddress.includes(':')) {
      return hwAddress.slice(hwAddress.length - 6, hwAddress.length);
    }

    return hwAddress;
  };

  const DefaultUserPermissions = {
    add: [
      { permission: 'organization:read' },
      { permission: 'property:read' },
      { permission: 'property:write' },
      { permission: 'device:read' },
      { permission: 'device:write' },
      { permission: 'field:read' },
      { permission: 'field:write' },
      { permission: 'user:read' },
      { permission: 'event:read' },
      { permission: 'event:write' },
      { permission: 'crop:read' },
      { permission: 'openid' },
      { permission: 'profile' },
      { permission: 'archive' },
      { permission: 'offline_access' },
    ],
  };

  const PlanTypes = {
    Harvest: 'Harvest',
    Plant: 'Plant',
    Fertilize: 'Fertilize',
    Irrigate: 'Irrigate',
    Clear: 'Clear',
    Treat: 'Treat',
  };

  const StatusCodes = {
    NoDeviceSelected: -1,
    NoFieldSelected: -2,
    NoPropertySelected: -3,
    NoOrganizationSelected: -4,
  };

  return {
    ReportEventToAnalytics,
    StatusCodes,
    PlanTypes,
    DefaultUserPermissions,
    exportData,
    getHighestRole,
    getTelemetryAverageStats,
    getDataSet,
    getEventsForLayers,
    exportDataSingle,
    getEventsAndSubordinates,
    getEventGlanceTooltipListByType,
    includesCaseInsensitive,
    setDefaultTelemetryPeriodByActiveTab,
    getObjectById,
    filteredFields,
    formatDateTime,
    flattenObject,
    validateFormField,
    getParentsOfLayer,
    sortTableFunction,
    getBoundsForField,
    getBounds,
    getIconForPlanType,
    searchProps,
    trimHWAddress,
    searchObject,
    getIconForRSSIValue,
    jsonToCSV,
  };
})();

export default DashboardHelpers;
